"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PackageCheck, Search, Camera, FileText, ArrowDownRight, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
interface InspectionStep {
  step: string;
  title: string;
  description: string;
  category: string;
}
const inspectionSteps: InspectionStep[] = [{
  step: "STEP 01",
  title: "Count the Packages",
  description: "Confirm that the number of delivered pieces matches the shipment information where provided.",
  category: "PIECE COUNT"
}, {
  step: "STEP 02",
  title: "Check External Condition",
  description: "Look for obvious damage to packaging before unpacking.",
  category: "SURFACE AUDIT"
}, {
  step: "STEP 03",
  title: "Photograph Concerns",
  description: "If external damage is visible, photographs can help document its condition at delivery.",
  category: "EVIDENCE LOG"
}, {
  step: "STEP 04",
  title: "Keep Shipment Information",
  description: "Retain the relevant delivery and order records while the equipment is unpacked and checked.",
  category: "RECORDS RETENTION"
}];
export default function Receiving() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const getStepIcon = (index: number) => {
    switch (index) {
      case 0:
        return <PackageCheck className="size-5 text-primary" />;
      case 1:
        return <Search className="size-5 text-primary" />;
      case 2:
        return <Camera className="size-5 text-primary" />;
      case 3:
      default:
        return <FileText className="size-5 text-primary" />;
    }
  };
  return <section id="receivingbulldelivery" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Specification Frame Header */}
        <div className="border border-border bg-card p-6 md:p-10 mb-8 sm:mb-12">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="rounded-none font-mono text-[11px] tracking-wider uppercase border-border bg-muted/50 px-2 py-0.5 text-muted-foreground">
                SOP-REC-01
              </Badge>
              <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase">
                // FREIGHT RECEIVING PROTOCOL
              </span>
            </div>
            <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
              <ShieldAlert className="size-3.5 text-primary" />
              <span>COMMERCIAL FREIGHT INTAKE</span>
            </div>
          </div>

          <div className="max-w-4xl">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground mb-4">
              Receiving a Mechanical Bull Delivery
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              When the shipment arrives, check the delivered packages before moving immediately into setup. Compare what arrived with the order information and look for obvious external damage that may have occurred during transportation.
            </p>
          </div>
        </div>

        {/* 4-Card Industrial Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-40px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {inspectionSteps.map((item, index) => <motion.div key={item.step} data-index={index} variants={cardVariants} className="group flex flex-col justify-between bg-card border border-border p-6 rounded-none transition-[border-color] duration-200 hover:border-foreground">
              <div>
                <div className="flex items-center justify-between border-b border-border pb-3 mb-6">
                  <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground">
                    {item.step}
                  </span>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground/80 bg-muted px-1.5 py-0.5">
                    {item.category}
                  </span>
                </div>

                <div className="size-10 flex items-center justify-center bg-muted border border-border mb-6">
                  {getStepIcon(index)}
                </div>

                <h3 className="font-serif text-xl sm:text-2xl font-semibold uppercase tracking-tight text-foreground mb-3">
                  {item.title}
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  {item.description}
                </p>
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-between text-muted-foreground group-hover:text-foreground transition-colors">
                <span className="font-mono text-[11px] uppercase tracking-wider">
                  CHECKPOINT 0{index + 1}
                </span>
                <ArrowDownRight className="size-4 opacity-40 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.div>)}
        </motion.div>

        {/* Technical Footer Indicator */}
        <div className="mt-8 pt-4 border-t border-border/70 flex flex-wrap items-center justify-between gap-4 font-mono text-xs text-muted-foreground">
          <span>SPECIFICATION STANDARD: US COMMERCIAL FREIGHT INTAKE</span>
          <span className="text-right">DOCUMENT STATUS: ACTIVE // NO UNAUTHORIZED MODIFICATIONS</span>
        </div>
      </div>
    </section>;
}